import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "motion/react";
import { WifiOff, AlertTriangle } from "lucide-react";
import Sidebar from "./components/Sidebar";
import InboxView from "./components/InboxView";
import MessageView from "./components/MessageView";
import ComposeModal, { ComposeData } from "./components/ComposeModal";
import SettingsModal from "./components/SettingsModal";
import EmptyState from "./components/EmptyState";
import CalendarView from "./components/CalendarView";
import AuthScreen from "./components/AuthScreen";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { ToastContainer, FullPageSpinner } from "./components/ui";
import { useTheme } from "./hooks/useTheme";
import { initCloudflareApi } from "./lib/cloudflare-api";
import { getAuthState, clearAllStorage } from "./lib/storage";

interface EmailDetail {
  id: string;
  sender: string;
  subject: string;
  body: string;
  date: string;
  attachments: string[];
  to?: string;
  cc?: string;
  bcc?: string;
  tags?: string[];
  is_draft?: boolean;
  read?: boolean;
  folder?: string;
  thread_id?: string;
  is_html?: boolean;
}

function App() {
  const { theme, changeTheme } = useTheme();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [selectedFolder, setSelectedFolder] = useState("inbox");
  const [selectedEmail, setSelectedEmail] = useState<string | null>(null);
  const [isComposing, setIsComposing] = useState(false);
  const [composeData, setComposeData] = useState<ComposeData | undefined>(undefined);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const state = getAuthState();
        setIsAuthenticated(state.logged_in);
        if (state.logged_in) {
          await initCloudflareApi();
        }
      } catch (error) {
        console.error("Error checking auth:", error);
        setIsAuthenticated(false);
      } finally {
        setCheckingAuth(false);
      }
    };
    checkAuth();
  }, []);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleFolderChange = (folder: string) => {
    setIsComposing(false);
    setIsSettingsOpen(false);
    setIsCalendarOpen(false);
    setSelectedFolder(folder);
    setSelectedEmail(null);
  };

  const handleEmailSent = () => {
    setIsComposing(false);
    setComposeData(undefined);
    setRefreshKey(prev => prev + 1);
  };

  const handleReply = (mode: 'reply' | 'replyAll' | 'forward', emailData: EmailDetail) => {
    const data: ComposeData = {
      mode,
      originalSender: emailData.sender,
      originalTo: emailData.to,
    };

    if (mode === 'reply') {
      data.to = emailData.sender;
      data.subject = emailData.subject.startsWith('Re:')
        ? emailData.subject
        : `Re: ${emailData.subject}`;
      data.body = `\n\n--- Mensaje original ---\nDe: ${emailData.sender}\nFecha: ${emailData.date}\nAsunto: ${emailData.subject}\n\n${emailData.body}`;
    } else if (mode === 'replyAll') {
      const recipients = [emailData.sender];
      if (emailData.to && emailData.to !== 'Yo') {
        recipients.push(emailData.to);
      }
      data.to = recipients.join(', ');
      data.subject = emailData.subject.startsWith('Re:')
        ? emailData.subject
        : `Re: ${emailData.subject}`;
      data.body = `\n\n--- Mensaje original ---\nDe: ${emailData.sender}\nPara: ${emailData.to || 'Yo'}\nFecha: ${emailData.date}\nAsunto: ${emailData.subject}\n\n${emailData.body}`;
    } else if (mode === 'forward') {
      data.to = '';
      data.subject = emailData.subject.startsWith('Fwd:')
        ? emailData.subject
        : `Fwd: ${emailData.subject}`;
      data.body = `\n\n--- Mensaje reenviado ---\nDe: ${emailData.sender}\nFecha: ${emailData.date}\nAsunto: ${emailData.subject}\n\n${emailData.body}`;
    }

    setComposeData(data);
    setIsComposing(true);
  };

  const handleEditDraft = (draftData: EmailDetail) => {
    const data: ComposeData = {
      to: draftData.to || '',
      subject: draftData.subject,
      body: draftData.body,
      draftId: draftData.id,
      cc: draftData.cc,
      bcc: draftData.bcc,
      attachments: draftData.attachments,
    };

    setComposeData(data);
    setIsComposing(true);
    setSelectedEmail(null);
  };

  const handleSearch = (query: string) => {
    handleFolderChange(`search:${query}`);
  };

  if (checkingAuth) {
    return <FullPageSpinner />;
  }

  if (!isAuthenticated) {
    return <AuthScreen onAuthSuccess={async () => {
      await initCloudflareApi();
      setIsAuthenticated(true);
    }} />;
  }

  const errorFallback = (
    <div className="error-boundary-fullscreen">
      <div className="error-boundary-content">
        <div className="error-boundary-icon" style={{ color: '#f97316' }}>
          <AlertTriangle size={64} strokeWidth={1.5} />
        </div>
        <h2>Algo salio mal</h2>
        <p>Ocurrio un error inesperado. Puedes intentar recargar la pagina o cerrar sesion.</p>
        <div className="error-boundary-actions">
          <button onClick={() => window.location.reload()} className="error-btn primary">
            Recargar pagina
          </button>
          <button onClick={() => {
            clearAllStorage();
            window.location.reload();
          }} className="error-btn secondary">
            Cerrar sesion
          </button>
        </div>
      </div>
    </div>
  );

  // Determine which main content to render
  const renderMainContent = () => {
    if (isComposing) {
      return (
        <motion.div
          key="compose"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          style={{ display: 'contents' }}
        >
          <ComposeModal onClose={handleEmailSent} initialData={composeData} />
        </motion.div>
      );
    }

    if (isCalendarOpen) {
      return (
        <motion.div
          key="calendar"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          style={{ display: 'contents' }}
        >
          <CalendarView onClose={() => setIsCalendarOpen(false)} />
        </motion.div>
      );
    }

    if (isSettingsOpen) {
      return (
        <motion.div
          key="settings"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          style={{ flex: 1, display: "flex", overflow: "hidden" }}
        >
          <SettingsModal
            onClose={() => setIsSettingsOpen(false)}
            theme={theme}
            onThemeChange={changeTheme}
            onLogout={() => {
              setIsSettingsOpen(false);
              setIsAuthenticated(false);
            }}
          />
        </motion.div>
      );
    }

    return (
      <motion.div
        key="inbox"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        style={{ display: 'contents' }}
      >
        <InboxView
          folder={selectedFolder}
          onSelectEmail={setSelectedEmail}
          onRefresh={() => {}}
          refreshTrigger={refreshKey}
          onSearch={handleSearch}
        />
        {selectedEmail ? (
          <MessageView
            emailId={selectedEmail}
            onClose={() => setSelectedEmail(null)}
            onDelete={() => {
              setSelectedEmail(null);
              setRefreshKey(prev => prev + 1);
            }}
            onReply={handleReply}
            onEditDraft={handleEditDraft}
            onNavigateToEmail={setSelectedEmail}
          />
        ) : (
          <EmptyState />
        )}
      </motion.div>
    );
  };

  return (
    <ErrorBoundary
      fallback={errorFallback}
      onError={(error, errorInfo) => {
        console.error('App Error Boundary caught:', error, errorInfo);
      }}
    >
      <>
        <ToastContainer />

        {/* Offline banner */}
        <AnimatePresence>
          {!isOnline && (
            <motion.div
              className="offline-banner"
              initial={{ opacity: 0, y: -36 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -36 }}
              transition={{ duration: 0.2 }}
            >
              <WifiOff size={14} strokeWidth={2.5} />
              Sin conexion a internet
            </motion.div>
          )}
        </AnimatePresence>

        <div className="app" style={{ marginTop: !isOnline ? '36px' : '0', transition: 'margin-top 0.2s ease' }}>
          <Sidebar
            selectedFolder={selectedFolder}
            onSelectFolder={handleFolderChange}
            onCompose={() => {
              setComposeData(undefined);
              setIsComposing(true);
              setIsCalendarOpen(false);
            }}
            onSettings={() => {
              setIsSettingsOpen(true);
              setIsCalendarOpen(false);
            }}
            onCalendar={() => {
              setIsCalendarOpen(true);
              setIsComposing(false);
              setIsSettingsOpen(false);
              setSelectedEmail(null);
            }}
            isOnline={isOnline}
          />
          <AnimatePresence mode="wait">
            {renderMainContent()}
          </AnimatePresence>
        </div>
      </>
    </ErrorBoundary>
  );
}

export default App;
